/**
 * @page /schichtplanung/wochenplan
 * @description Konsolidierter Wochenplan — Wochen-Übersicht + Tages-Detail + Rotation.
 *   Ersetzt separate Tages-Einteilung und Kunden-Rotation Seiten (v2.223.0).
 * @api GET /api/schichtplanung/wochenplan
 * @api POST /api/schichtplanung/wochenplan/generieren
 * @api PUT /api/schichtplanung/wochenplan/:id
 * @api GET /api/schichtplanung/wochenplan/tages-detail/:datum
 * @api POST /api/schichtplanung/wochenplan/tages-detail
 * @roles admin, manager
 * @i18n schichtplanung.wochenplan
 * @since v2.210.0, consolidated v2.223.0
 */
import { useState, useEffect, useCallback, useMemo, lazy, Suspense } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, RefreshCw, AlertTriangle, X, Plus, Save, Sparkles, Trash2, ArrowLeft, Settings } from 'lucide-react';
import ModulEinstellungen from '../components/ModulEinstellungen';
import { PageHeader } from '../components/ui';
import { useLanguage } from '../context/LanguageContext';

const KundenRotation = lazy(() => import('./KundenRotation'));

const API = import.meta.env.VITE_API_URL || '';

const WOCHENTAGE_DE = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
const WOCHENTAGE_EN = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const STATUS_CONFIG = {
  verfuegbar:    { label_de: 'Verfügbar',     label_en: 'Available',  color: '#22c55e', bg: '#22c55e1a', icon: '✓' },
  urlaub:        { label_de: 'Urlaub',         label_en: 'Vacation',   color: '#338edc', bg: '#338edc1a', icon: '🏖' },
  krank:         { label_de: 'Krank',          label_en: 'Sick',       color: '#f59e0b', bg: '#f59e0b1a', icon: '🤒' },
  zeitausgleich: { label_de: 'Zeitausgleich',  label_en: 'Comp time',  color: '#8b5cf6', bg: '#8b5cf61a', icon: 'ZA' },
  sonderurlaub:  { label_de: 'Sonderurlaub',   label_en: 'Special',    color: '#6366f1', bg: '#6366f11a', icon: 'SU' },
  frei:          { label_de: 'Frei',           label_en: 'Off',        color: '#9ca3af', bg: '#9ca3af1a', icon: '—' },
};

const BEREICH_LABELS = {
  outbound: 'Outbound',
  inbound: 'Inbound & Retouren',
  verwaltung: 'Verwaltung',
  _abwesend: 'Abwesend',
};

function getMonday(d) {
  const date = new Date(d);
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);
  date.setDate(diff);
  return date;
}

function ds(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function getKW(d) {
  const date = new Date(d);
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + 3 - (date.getDay() + 6) % 7);
  const week1 = new Date(date.getFullYear(), 0, 4);
  return 1 + Math.round(((date - week1) / 86400000 - 3 + (week1.getDay() + 6) % 7) / 7);
}

function formatDateShort(dateString, lang) {
  if (!dateString) return '';
  const d = new Date(dateString + 'T12:00:00Z');
  const wt = lang === 'de' ? WOCHENTAGE_DE : WOCHENTAGE_EN;
  const monthNames_de = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
  const monthNames_en = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const mNames = lang === 'de' ? monthNames_de : monthNames_en;
  return `${wt[d.getUTCDay()]} ${d.getUTCDate()}. ${mNames[d.getUTCMonth()]}`;
}

function formatDateFull(dateString, lang) {
  if (!dateString) return '';
  const d = new Date(dateString + 'T00:00:00');
  return d.toLocaleDateString(lang === 'de' ? 'de-AT' : 'en-US', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

function shortName(name) {
  if (!name) return '';
  const parts = name.split(' ');
  return parts.length < 2 ? name : `${parts[0]} ${parts[parts.length - 1][0]}.`;
}

function getInitials(name) {
  if (!name) return '?';
  const parts = name.split(' ').filter(Boolean);
  if (parts.length < 2) return (parts[0]?.[0] || '?').toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const BEREICH_COLORS = {
  outbound:   { bg: '#338edc1a', text: '#338edc' },
  inbound:    { bg: '#6366f11a', text: '#6366f1' },
  verwaltung: { bg: '#22c55e1a', text: '#22c55e' },
  _abwesend:  { bg: '#9ca3af1a', text: '#9ca3af' },
};

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════
export default function WochenplanPage() {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initTab = searchParams.get('tab') || 'wochenplan';
  const initDetail = searchParams.get('datum') || null;

  const [activeTab, setActiveTab] = useState(initTab);
  const [monday, setMonday] = useState(() => getMonday(new Date()));
  const [weekCount, setWeekCount] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [editCell, setEditCell] = useState(null);
  const [detailDatum, setDetailDatum] = useState(initDetail);

  // Build list of weekdays based on weekCount
  const wochentage = useMemo(() => {
    const days = [];
    for (let i = 0; i < weekCount * 5; i++) {
      const d = new Date(monday);
      // Skip weekends
      const weekNum = Math.floor(i / 5);
      const dayInWeek = i % 5;
      d.setDate(monday.getDate() + weekNum * 7 + dayInWeek);
      days.push(ds(d));
    }
    return days;
  }, [monday, weekCount]);

  const vonDatum = wochentage[0];
  const bisDatum = wochentage[wochentage.length - 1];
  const kw = getKW(monday);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/schichtplanung/wochenplan?datum_von=${vonDatum}&datum_bis=${bisDatum}`, { credentials: 'include' });
      if (res.ok) setData(await res.json());
    } catch { /* ignore */ }
    setLoading(false);
  }, [vonDatum, bisDatum]);

  useEffect(() => { load(); }, [load]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await fetch(`${API}/api/schichtplanung/wochenplan/generieren`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ datum_von: vonDatum, datum_bis: bisDatum }),
      });
      if (res.ok) await load();
    } catch { /* ignore */ }
    setGenerating(false);
  };

  const handleCellUpdate = async (id, field, value) => {
    try {
      await fetch(`${API}/api/schichtplanung/wochenplan/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ [field]: value }),
      });
      setEditCell(null);
      await load();
    } catch { /* ignore */ }
  };

  const prevWeek = () => setMonday(prev => { const d = new Date(prev); d.setDate(d.getDate() - 7); return d; });
  const nextWeek = () => setMonday(prev => { const d = new Date(prev); d.setDate(d.getDate() + 7); return d; });
  const goToday = () => setMonday(getMonday(new Date()));

  // Open day detail
  const openDayDetail = (datum) => {
    setDetailDatum(datum);
    setActiveTab('detail');
    setSearchParams({ tab: 'detail', datum });
  };

  const closeDayDetail = () => {
    setDetailDatum(null);
    setActiveTab('wochenplan');
    setSearchParams({});
  };

  // Update URL on tab change
  const switchTab = (tab) => {
    setActiveTab(tab);
    if (tab === 'wochenplan') setSearchParams({});
    else if (tab === 'rotation') setSearchParams({ tab: 'rotation' });
    // detail tab is set via openDayDetail
  };

  const bereiche = data?.bereiche || {};
  const flexWorker = data?.flex_worker || [];
  const minBesetzung = data?.min_besetzung || {};

  const bereichOrder = ['outbound', 'inbound', 'verwaltung', '_abwesend'];
  const sortedBereiche = bereichOrder.filter(b => bereiche[b]).map(b => [b, bereiche[b]]);
  for (const [key, val] of Object.entries(bereiche)) {
    if (!bereichOrder.includes(key)) sortedBereiche.push([key, val]);
  }

  function countVerfuegbar(bereichKey, datum) {
    const b = bereiche[bereichKey];
    if (!b) return 0;
    return b.mitarbeiter.filter(ma => ma.tage[datum]?.status === 'verfuegbar').length;
  }

  const isCompact = weekCount > 1;
  const TABS = [
    { key: 'wochenplan', label: t('schichtplanung.wochenplan.tabWochenplan', 'Wochenplan') },
    { key: 'detail', label: detailDatum ? `${t('schichtplanung.wochenplan.tagesDetail', 'Tages-Detail')} — ${formatDateShort(detailDatum, language)}` : t('schichtplanung.wochenplan.tagesDetail', 'Tages-Detail') },
    { key: 'rotation', label: t('schichtplanung.wochenplan.rotation', 'Rotation') },
    { key: 'einstellungen', label: t('modulSettings.title', 'Einstellungen') },
  ];

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <PageHeader title={t('schichtplanung.wochenplan.title', 'Wochenplan')} />
        <div className="flex items-center gap-3">
          {activeTab === 'wochenplan' && (
            <>
              {/* Week count toggle */}
              <div className="bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] border border-[var(--border-default,#E5E7EB)] inline-flex gap-0.5">
                {[1, 2, 4].map(n => (
                  <button key={n} onClick={() => setWeekCount(n)}
                    className={`px-2.5 py-1 rounded-[8px] text-xs font-medium transition-all ${
                      weekCount === n
                        ? 'bg-white shadow-sm text-[var(--text-primary)] font-semibold'
                        : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
                    }`}>
                    {n === 1 ? `1 ${t('schichtplanung.wochenplan.woche', 'Woche')}` : `${n} ${t('schichtplanung.wochenplan.wochen', 'Wochen')}`}
                  </button>
                ))}
              </div>

              <div className="flex items-center gap-1 border border-[var(--border-default,#E5E7EB)] rounded-lg overflow-hidden">
                <button onClick={prevWeek} className="px-2 py-1.5 hover:bg-[var(--bg-hover,#F9FAFB)] transition-colors">
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button onClick={goToday} className="px-3 py-1.5 text-sm font-medium hover:bg-[var(--bg-hover,#F9FAFB)] transition-colors">
                  {t('schichtplanung.wochenplan.heute', 'Heute')}
                </button>
                <button onClick={nextWeek} className="px-2 py-1.5 hover:bg-[var(--bg-hover,#F9FAFB)] transition-colors">
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
              <span className="text-sm font-semibold text-[var(--text-primary)]">
                {t('schichtplanung.wochenplan.kw', 'KW')} {kw}{weekCount > 1 ? `–${getKW(new Date(new Date(monday).setDate(monday.getDate() + (weekCount - 1) * 7)))}` : ''}
              </span>
              <button onClick={handleGenerate} disabled={generating}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-white bg-[#338edc] hover:bg-[#2a7bc8] transition-colors disabled:opacity-50">
                <RefreshCw className={`w-4 h-4 ${generating ? 'animate-spin' : ''}`} />
                {t('schichtplanung.wochenplan.generieren', 'Auto-Generieren')}
              </button>
            </>
          )}
          {activeTab === 'detail' && (
            <button onClick={closeDayDetail}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border border-[var(--border-default,#E5E7EB)] hover:bg-[var(--bg-hover,#F9FAFB)] text-[var(--text-primary)] transition-colors">
              <ArrowLeft className="w-4 h-4" />
              {t('schichtplanung.wochenplan.zurueck', 'Zurück')}
            </button>
          )}
        </div>
      </div>

      {/* Tab Bar */}
      <div className="bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] border border-[var(--border-default,#E5E7EB)] inline-flex gap-1 mb-5">
        {TABS.map(tab => (
          <button key={tab.key}
            onClick={() => tab.key === 'detail' ? (detailDatum ? switchTab('detail') : null) : switchTab(tab.key)}
            className={`px-4 py-1.5 rounded-[8px] text-sm font-medium transition-all ${
              activeTab === tab.key
                ? 'bg-white shadow-sm text-[var(--text-primary)] font-semibold'
                : tab.key === 'detail' && !detailDatum
                ? 'text-[var(--text-muted)] cursor-default'
                : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
            }`}
            disabled={tab.key === 'detail' && !detailDatum}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* ═══ TAB: Wochenplan ═══ */}
      {activeTab === 'wochenplan' && (
        <>
          {/* Legend */}
          <div className="flex items-center gap-4 mb-4 text-xs text-[var(--text-tertiary)]">
            {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
              <span key={key} className="flex items-center gap-1">
                <span className="w-3 h-3 rounded" style={{ background: cfg.bg, border: `1px solid ${cfg.color}` }} />
                {language === 'de' ? cfg.label_de : cfg.label_en}
              </span>
            ))}
          </div>

          {loading && (
            <div className="text-center py-12 text-[var(--text-tertiary)]">{t('common.loading', 'Laden...')}</div>
          )}

          {!loading && sortedBereiche.length === 0 && (
            <div className="text-center py-16 text-[var(--text-secondary)]">
              <p className="mb-3">{t('schichtplanung.wochenplan.leer', 'Noch kein Wochenplan vorhanden.')}</p>
              <button onClick={handleGenerate}
                className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-[#338edc] hover:bg-[#2a7bc8]">
                {t('schichtplanung.wochenplan.generieren', 'Auto-Generieren')}
              </button>
            </div>
          )}

          {/* Bereich Sections */}
          {sortedBereiche.map(([bereichKey, bereich]) => {
            const minCount = minBesetzung[bereichKey];
            return (
              <div key={bereichKey} className="mb-5">
                <div className="flex items-center gap-2 mb-2">
                  <h2 className="text-sm font-semibold text-[var(--text-primary)]">
                    {BEREICH_LABELS[bereichKey] || bereich.label || bereichKey}
                  </h2>
                  <span className="text-xs text-[var(--text-tertiary)]">
                    ({bereich.mitarbeiter.length} {t('schichtplanung.wochenplan.ma', 'MA')})
                  </span>
                </div>

                <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl overflow-x-auto">
                  <table className="w-full text-sm table-fixed">
                    <thead>
                      <tr className="bg-[var(--bg-inset,#F8F9FC)]">
                        <th className="text-left px-2 py-2 font-medium text-[var(--text-secondary)] w-[140px] sticky left-0 bg-[var(--bg-inset,#F8F9FC)] z-10">
                          {t('schichtplanung.wochenplan.name', 'Name')}
                        </th>
                        {wochentage.map(d => {
                          const verf = countVerfuegbar(bereichKey, d);
                          const unterbesetzt = minCount && verf < minCount;
                          return (
                            <th key={d} className={`text-center px-1 py-2 font-medium text-[var(--text-secondary)] ${unterbesetzt ? 'text-red-600' : ''}`}>
                              <button onClick={() => openDayDetail(d)} className="hover:text-[#338edc] transition-colors">
                                {formatDateShort(d, language)}
                              </button>
                              {minCount && bereichKey !== '_abwesend' && !isCompact && (
                                <div className={`text-[10px] font-normal ${unterbesetzt ? 'text-red-500' : 'text-[var(--text-muted)]'}`} title={`${verf} ${t('schichtplanung.wochenplan.verfuegbar', 'available')} / ${minCount} ${t('schichtplanung.wochenplan.min', 'min')}`}>
                                  {verf} / {minCount} MA
                                  {unterbesetzt && <AlertTriangle className="w-3 h-3 inline ml-0.5 -mt-0.5" />}
                                </div>
                              )}
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {bereich.mitarbeiter.map((ma, idx) => {
                        const bColors = BEREICH_COLORS[bereichKey] || BEREICH_COLORS.outbound;
                        return (
                        <tr key={ma.id} className={idx % 2 === 0 ? '' : 'bg-[var(--bg-inset,#F8F9FC)]/50'}>
                          <td className="px-2 py-1 sticky left-0 bg-[var(--bg-card,#fff)] z-10">
                            <button onClick={() => navigate(`/mitarbeiter/${ma.id}`)}
                              className="flex items-center gap-2 hover:text-[#338edc] transition-colors text-left w-full group"
                              title={ma.name}>
                              <span className="w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center text-[10px] font-bold"
                                style={{ background: bColors.bg, color: bColors.text }}>
                                {getInitials(ma.name)}
                              </span>
                              <span className="truncate min-w-0">
                                <span className="block text-xs font-medium text-[var(--text-primary)] group-hover:text-[#338edc] truncate">{shortName(ma.name)}</span>
                                {!isCompact && ma.wochenstunden_soll && (
                                  <span className="block text-[10px] text-[var(--text-muted)] leading-tight">{parseFloat(ma.wochenstunden_soll)}h/{t('schichtplanung.wochenplan.woche', 'Woche')}</span>
                                )}
                              </span>
                            </button>
                          </td>
                          {wochentage.map(d => {
                            const tag = ma.tage[d];
                            if (!tag) return <td key={d} className="text-center px-1 py-1 text-[var(--text-muted)]">—</td>;
                            const cfg = STATUS_CONFIG[tag.status] || STATUS_CONFIG.verfuegbar;
                            const isEditing = editCell?.id === tag.id;
                            const hasKunden = tag.kunden && tag.kunden.length > 0;
                            return (
                              <td key={d} className="text-center px-0.5 py-0.5">
                                {isEditing ? (
                                  <select autoFocus className="text-xs rounded border px-1 py-0.5 bg-white"
                                    defaultValue={tag.status}
                                    onChange={e => handleCellUpdate(tag.id, 'status', e.target.value)}
                                    onBlur={() => setEditCell(null)}>
                                    {Object.entries(STATUS_CONFIG).map(([k, c]) => (
                                      <option key={k} value={k}>{language === 'de' ? c.label_de : c.label_en}</option>
                                    ))}
                                  </select>
                                ) : (
                                  <button onClick={() => setEditCell({ id: tag.id, maId: ma.id, datum: d })}
                                    onDoubleClick={() => openDayDetail(d)}
                                    className={`w-full py-1 rounded-lg text-xs font-medium transition-colors ${isCompact ? 'text-[10px]' : ''}`}
                                    style={{ background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.color}30` }}
                                    title={`${language === 'de' ? cfg.label_de : cfg.label_en}${hasKunden ? ` — ${tag.kunden.map(k => k.kunde_name).join(', ')}` : ''}${tag.notiz ? ` — ${tag.notiz}` : ''}${tag.quelle === 'manuell' ? ' (manuell)' : ''}`}>
                                    {isCompact ? cfg.icon : (
                                      <>
                                        {cfg.icon}
                                        {hasKunden && <span className="ml-0.5 text-[9px] opacity-70">({tag.kunden.length})</span>}
                                      </>
                                    )}
                                  </button>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}

          {/* Flex-Worker */}
          {flexWorker.length > 0 && (
            <div className="mb-5">
              <div className="flex items-center gap-2 mb-2">
                <h2 className="text-sm font-semibold text-[var(--text-primary)]">
                  {t('schichtplanung.wochenplan.flex', 'Flex-Worker')}
                </h2>
                <span className="text-xs text-[var(--text-tertiary)]">({flexWorker.length})</span>
              </div>
              <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl overflow-x-auto">
                <table className="w-full text-sm table-fixed">
                  <thead>
                    <tr className="bg-[var(--bg-inset,#F8F9FC)]">
                      <th className="text-left px-2 py-2 font-medium text-[var(--text-secondary)] w-[140px] sticky left-0 bg-[var(--bg-inset,#F8F9FC)]">
                        {t('schichtplanung.wochenplan.name', 'Name')}
                      </th>
                      {wochentage.map(d => (
                        <th key={d} className={`text-center px-1 py-2 font-medium text-[var(--text-secondary)]`}>
                          {formatDateShort(d, language)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {flexWorker.map((fw, idx) => (
                      <tr key={`${fw.bewerber_id}-${idx}`}>
                        <td className="px-3 py-1.5 font-medium text-[var(--text-primary)] text-xs sticky left-0 bg-[var(--bg-card,#fff)]">{fw.name}</td>
                        {wochentage.map(d => {
                          const isActive = d >= fw.datum_von && d <= fw.datum_bis;
                          return (
                            <td key={d} className="text-center px-0.5 py-0.5">
                              {isActive ? (
                                <span className={`inline-block w-full py-1 rounded-lg font-medium ${isCompact ? 'text-[10px]' : 'text-xs'}`} style={{ background: '#06b6d41a', color: '#06b6d4', border: '1px solid #06b6d430' }}>
                                  F
                                </span>
                              ) : (
                                <span className="text-[var(--text-muted)]">—</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* ═══ TAB: Tages-Detail ═══ */}
      {activeTab === 'detail' && detailDatum && (
        <TagesDetailView datum={detailDatum} onBack={closeDayDetail} onReload={load} />
      )}

      {/* ═══ TAB: Rotation ═══ */}
      {activeTab === 'rotation' && (
        <Suspense fallback={<div className="text-center py-12 text-[var(--text-tertiary)]">{t('common.loading', 'Laden...')}</div>}>
          <KundenRotation />
        </Suspense>
      )}

      {/* ═══ TAB: Einstellungen ═══ */}
      {activeTab === 'einstellungen' && (
        <ModulEinstellungen kategorien={['schichtplanung']} />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// TAGES-DETAIL VIEW
// ═══════════════════════════════════════════════════════════════
function TagesDetailView({ datum, onBack, onReload }) {
  const { t, language } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [zuweisungen, setZuweisungen] = useState([]); // [{wp_id, ma_id, ma_name, lager_rolle, kunden, anweisung}]
  const [unassigned, setUnassigned] = useState([]);
  const [assignModal, setAssignModal] = useState(null);
  const [echtzeit, setEchtzeit] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/schichtplanung/wochenplan/tages-detail/${datum}`, { credentials: 'include' });
      if (res.ok) {
        const json = await res.json();
        setData(json);
        buildZuweisungen(json);
      }
    } catch { /* ignore */ }
    setLoading(false);
  }, [datum]);

  const loadEchtzeit = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/echtzeit/status/${datum}`, { credentials: 'include' });
      if (res.ok) setEchtzeit(await res.json());
    } catch { /* silent */ }
  }, [datum]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    loadEchtzeit();
    const iv = setInterval(loadEchtzeit, 60000);
    return () => clearInterval(iv);
  }, [loadEchtzeit]);

  function buildZuweisungen(d) {
    if (!d) return;
    const assigned = [];
    const unassign = [];
    for (const ma of d.verfuegbar) {
      if (ma.kunden && ma.kunden.length > 0) {
        assigned.push({
          wp_id: ma.wp_id,
          ma_id: ma.id,
          ma_name: ma.name,
          lager_rolle: ma.lager_rolle,
          kunden: ma.kunden,
          anweisung: ma.anweisung || '',
        });
      } else {
        unassign.push(ma);
      }
    }
    setZuweisungen(assigned);
    setUnassigned(unassign);
    setDirty(false);
  }

  // Assign MA to kunden
  function assignMa(ma, kundenList) {
    setDirty(true);
    setZuweisungen(prev => [...prev, {
      wp_id: ma.wp_id,
      ma_id: ma.id,
      ma_name: ma.name,
      lager_rolle: ma.lager_rolle,
      kunden: kundenList,
      anweisung: '',
    }]);
    setUnassigned(prev => prev.filter(u => u.id !== ma.id));
    setAssignModal(null);
  }

  // Remove MA from assignments
  function removeMa(wpId) {
    setDirty(true);
    const entry = zuweisungen.find(z => z.wp_id === wpId);
    if (entry) {
      setUnassigned(prev => [...prev, { wp_id: entry.wp_id, id: entry.ma_id, name: entry.ma_name, lager_rolle: entry.lager_rolle }]);
    }
    setZuweisungen(prev => prev.filter(z => z.wp_id !== wpId));
  }

  // Update kunden for assignment
  function updateKunden(wpId, kundenList) {
    setDirty(true);
    setZuweisungen(prev => prev.map(z => z.wp_id === wpId ? { ...z, kunden: kundenList } : z));
  }

  // Update anweisung
  function updateAnweisung(wpId, text) {
    setDirty(true);
    setZuweisungen(prev => prev.map(z => z.wp_id === wpId ? { ...z, anweisung: text } : z));
  }

  // Save all
  async function handleSave() {
    setSaving(true);
    try {
      // Save assigned + clear unassigned
      const allUpdates = [
        ...zuweisungen.map(z => ({ wp_id: z.wp_id, kunden: z.kunden, anweisung: z.anweisung || null })),
        ...unassigned.map(u => ({ wp_id: u.wp_id, kunden: [], anweisung: null })),
      ];
      await fetch(`${API}/api/schichtplanung/wochenplan/tages-detail`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ zuweisungen: allUpdates }),
      });
      setDirty(false);
      onReload();
    } catch { /* ignore */ }
    setSaving(false);
  }

  // Vorschlag from einteilung endpoint
  async function handleVorschlag() {
    try {
      const res = await fetch(`${API}/api/einteilung/${datum}/vorschlag`, { credentials: 'include' });
      const json = await res.json();
      if (!json.vorschlag || json.vorschlag.length === 0) return;

      const allMa = data?.verfuegbar || [];
      const newZuweisungen = [];
      const assignedIds = new Set();

      for (const v of json.vorschlag) {
        const ma = allMa.find(m => m.id === v.mitarbeiter_id);
        if (!ma || assignedIds.has(ma.id)) continue;
        assignedIds.add(ma.id);

        // Check if already in newZuweisungen
        const existing = newZuweisungen.find(z => z.wp_id === ma.wp_id);
        if (existing) {
          // Add kunde to existing
          for (const k of (v.kunden || [])) {
            if (!existing.kunden.find(ek => ek.kunde_id === k.kunde_id)) {
              existing.kunden.push(k);
            }
          }
        } else {
          newZuweisungen.push({
            wp_id: ma.wp_id,
            ma_id: ma.id,
            ma_name: ma.name,
            lager_rolle: ma.lager_rolle,
            kunden: v.kunden || [],
            anweisung: '',
          });
        }
      }

      setZuweisungen(newZuweisungen);
      setUnassigned(allMa.filter(m => !assignedIds.has(m.id)));
      setDirty(true);
    } catch { /* ignore */ }
  }

  const kunden = data?.kunden || [];

  // Group zuweisungen by rolle
  const pickZuweisungen = zuweisungen.filter(z => z.lager_rolle === 'picker' || z.lager_rolle === 'pick_pack');
  const packZuweisungen = zuweisungen.filter(z => z.lager_rolle === 'packer' || z.lager_rolle === 'pick_pack');
  // For pick_pack, show in both — but we actually just show all in one flat list since the wochenplan doesn't have rolle
  // Let's show by bereich logic: just show all together, with lager_rolle badge

  const STATUS_COLORS = {
    im_plan: { bg: 'bg-green-100', text: 'text-green-700' },
    leicht_hinten: { bg: 'bg-amber-100', text: 'text-amber-700' },
    kritisch_hinten: { bg: 'bg-red-100', text: 'text-red-700' },
    ueber_plan: { bg: 'bg-blue-100', text: 'text-blue-700' },
  };

  if (loading) {
    return <div className="text-center py-12 text-[var(--text-tertiary)]">{t('common.loading', 'Laden...')}</div>;
  }

  return (
    <div className="space-y-5">
      {/* Day header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-[var(--text-primary)]">{formatDateFull(datum, language)}</h2>
          <p className="text-sm text-[var(--text-tertiary)] mt-0.5">
            {zuweisungen.length} {t('schichtplanung.wochenplan.zugeteilt', 'assigned')} · {unassigned.length} {t('schichtplanung.wochenplan.verfuegbar', 'available')} · {kunden.length} {t('einteilung.kunden', 'customers')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleVorschlag}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border border-[var(--border-default,#E5E7EB)] hover:bg-[var(--bg-hover,#F9FAFB)] text-[var(--text-primary)] transition-colors">
            <Sparkles className="w-4 h-4" /> {t('einteilung.vorschlag', 'Generate Suggestion')}
          </button>
          <button onClick={handleSave} disabled={!dirty || saving}
            className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-semibold text-white bg-[#338edc] hover:bg-[#2b7bc4] disabled:opacity-50 transition-colors">
            <Save className="w-4 h-4" /> {t('einteilung.speichern', 'Save')}
          </button>
        </div>
      </div>

      {/* Echtzeit KPIs */}
      {echtzeit && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: t('echtzeit.soll', 'Target'), value: echtzeit.gesamt?.soll, sub: `${echtzeit.gesamt?.kunden_count} ${t('einteilung.kunden')}` },
            { label: t('echtzeit.ist', 'Actual'), value: echtzeit.gesamt?.ist, sub: `${echtzeit.gesamt?.offen} ${t('echtzeit.offen')}` },
            { label: t('echtzeit.prognose', 'Forecast'), value: echtzeit.gesamt?.prognose, sub: `${echtzeit.schicht?.ende} Uhr` },
            { label: t('echtzeit.schicht', 'Shift'), value: `${echtzeit.schicht?.vergangen_h}h`, sub: `${echtzeit.schicht?.verbleibend_h}h ${t('echtzeit.verbleibend')}` },
          ].map((kpi, i) => (
            <div key={i} className="border border-[var(--border-default,#E5E7EB)] rounded-xl p-4">
              <p className="text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)] mb-1">{kpi.label}</p>
              <p className="text-xl font-bold text-[var(--text-primary)]">{kpi.value ?? '—'}</p>
              <p className="text-xs text-[var(--text-muted)] mt-0.5">{kpi.sub}</p>
            </div>
          ))}
        </div>
      )}

      {/* Kunden-Forecast bar */}
      {kunden.length > 0 && (
        <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl p-4">
          <p className="text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)] mb-3">
            {t('einteilung.kundenForecast', 'Customers & Forecast')}
          </p>
          <div className="flex flex-wrap gap-2">
            {kunden.map(k => {
              const sc = echtzeit?.kunden?.find(ek => ek.kunde_id === k.id);
              const statusCfg = sc ? STATUS_COLORS[sc.prognose_status] : null;
              return (
                <div key={k.id} className="flex items-center gap-2 px-3 py-1.5 border border-[var(--border-default,#E5E7EB)] rounded-lg">
                  <span className="text-sm font-medium text-[var(--text-primary)]">{k.name}</span>
                  <span className="text-xs text-[var(--text-tertiary)]">{Math.round(k.soll_pakete)} {t('einteilung.pkt', 'pkg')}</span>
                  {sc && (
                    <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded-full ${statusCfg?.bg || ''} ${statusCfg?.text || ''}`}>
                      {sc.fortschritt_pct}%
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Zuweisungen */}
      <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <p className="text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]">
            {t('schichtplanung.wochenplan.kundenZuweisung', 'Customer Assignments')} ({zuweisungen.length})
          </p>
        </div>

        {zuweisungen.length === 0 ? (
          <p className="text-sm text-[var(--text-muted)] italic py-4 text-center">{t('einteilung.keineZuweisung', 'No assignments yet')}</p>
        ) : (
          <div className="space-y-3">
            {zuweisungen.map(z => (
              <div key={z.wp_id} className="border border-[var(--border-default,#E5E7EB)] rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-[var(--text-primary)]">{shortName(z.ma_name)}</span>
                    <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded-full ${
                      z.lager_rolle === 'picker' ? 'bg-blue-100 text-blue-700' :
                      z.lager_rolle === 'packer' ? 'bg-indigo-100 text-indigo-700' :
                      'bg-gray-100 text-[var(--text-secondary)]'
                    }`}>
                      {z.lager_rolle === 'pick_pack' ? 'PICK/PACK' : z.lager_rolle?.toUpperCase()}
                    </span>
                  </div>
                  <button onClick={() => removeMa(z.wp_id)}
                    className="p-1 text-[var(--text-muted)] hover:text-red-500 transition-colors">
                    <Trash2 size={14} />
                  </button>
                </div>

                {/* Kunden chips */}
                <div className="flex flex-wrap gap-2 mb-2">
                  {z.kunden.map(k => (
                    <span key={k.kunde_id}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[var(--bg-inset,#F8F9FC)] text-xs text-[var(--text-primary)] border border-[var(--border-default,#E5E7EB)]">
                      {k.kunde_name}
                      {kunden.find(kk => kk.id === k.kunde_id) && (
                        <span className="text-[var(--text-muted)]">({Math.round(kunden.find(kk => kk.id === k.kunde_id)?.soll_pakete || 0)})</span>
                      )}
                      <button onClick={() => updateKunden(z.wp_id, z.kunden.filter(kk => kk.kunde_id !== k.kunde_id))}
                        className="text-[var(--text-muted)] hover:text-red-500 ml-0.5">
                        <X size={10} />
                      </button>
                    </span>
                  ))}
                  <button onClick={() => setAssignModal({ type: 'add_kunde', wpId: z.wp_id, existingKunden: z.kunden })}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-full border border-dashed border-[var(--border-default,#E5E7EB)] text-[10px] text-[var(--text-muted)] hover:border-[#338edc] hover:text-[#338edc] transition-colors">
                    <Plus size={10} /> {t('schichtplanung.wochenplan.kundeHinzufuegen', 'Add customer')}
                  </button>
                </div>

                {/* Anweisung */}
                <input type="text"
                  placeholder={t('einteilung.anweisungPlaceholder', 'Instructions...')}
                  value={z.anweisung}
                  onChange={e => updateAnweisung(z.wp_id, e.target.value)}
                  className="w-full px-3 py-1.5 border border-[var(--border-default,#E5E7EB)] rounded-lg text-sm bg-[var(--bg-card,#fff)] text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:border-[#338edc] focus:ring-1 focus:ring-[#338edc]/20"
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Nicht zugeteilt */}
      {unassigned.length > 0 && (
        <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl p-5">
          <p className="text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)] mb-3">
            {t('einteilung.nichtZugeteilt', 'Not Assigned')} ({unassigned.length})
          </p>
          <div className="flex flex-wrap gap-2">
            {unassigned.map(ma => (
              <button key={ma.id}
                onClick={() => setAssignModal({ type: 'assign_ma', ma })}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[var(--border-default,#E5E7EB)] text-sm text-[var(--text-primary)] hover:border-[#338edc] hover:text-[#338edc] transition-colors">
                <Plus size={14} />
                {shortName(ma.name)}
                <span className={`px-1 py-0 text-[9px] font-medium rounded ${
                  ma.lager_rolle === 'picker' ? 'bg-blue-100 text-blue-700' :
                  ma.lager_rolle === 'packer' ? 'bg-indigo-100 text-indigo-700' :
                  'bg-gray-100 text-[var(--text-secondary)]'
                }`}>
                  {ma.lager_rolle === 'pick_pack' ? 'P/P' : ma.lager_rolle?.[0]?.toUpperCase()}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Empty state */}
      {kunden.length === 0 && unassigned.length === 0 && zuweisungen.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <h3 className="text-base font-semibold text-[var(--text-primary)] mb-1">
            {t('einteilung.keineVerfuegbar', 'No Data')}
          </h3>
          <p className="text-sm text-[var(--text-tertiary)] max-w-sm">
            {t('einteilung.keineVerfuegbarDesc', 'No employees or forecasts available for this day.')}
          </p>
        </div>
      )}

      {/* Assign Modal */}
      {assignModal && (
        <AssignModal
          type={assignModal.type}
          ma={assignModal.ma}
          wpId={assignModal.wpId}
          existingKunden={assignModal.existingKunden}
          kunden={kunden}
          onAssignMa={(ma, kundenList) => assignMa(ma, kundenList)}
          onAddKunde={(wpId, kunde) => {
            const entry = zuweisungen.find(z => z.wp_id === wpId);
            if (entry) {
              updateKunden(wpId, [...entry.kunden, kunde]);
            }
            setAssignModal(null);
          }}
          onClose={() => setAssignModal(null)}
          t={t}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// ASSIGN MODAL
// ═══════════════════════════════════════════════════════════════
function AssignModal({ type, ma, wpId, existingKunden = [], kunden, onAssignMa, onAddKunde, onClose, t }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
      <div className="bg-[var(--bg-card,#fff)] rounded-2xl border border-[var(--border-card,#E5E7EB)] shadow-xl w-full max-w-sm mx-4"
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--border-card,#E5E7EB)]">
          <h2 className="text-base font-semibold text-[var(--text-primary)]">
            {type === 'assign_ma'
              ? `${shortName(ma?.name)} ${t('einteilung.zuweisen', 'assign')}`
              : t('schichtplanung.wochenplan.kundeHinzufuegen', 'Add customer')
            }
          </h2>
          <button onClick={onClose} className="text-[var(--text-tertiary)] hover:text-[var(--text-primary)]">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-1.5 max-h-[300px] overflow-y-auto">
          {kunden.length === 0 ? (
            <p className="text-sm text-[var(--text-muted)] text-center py-4">{t('einteilung.keineKunden', 'No customers')}</p>
          ) : (
            kunden
              .filter(k => type === 'add_kunde' ? !existingKunden.find(ek => ek.kunde_id === k.id) : true)
              .map(k => (
                <button key={k.id}
                  onClick={() => {
                    if (type === 'assign_ma') {
                      onAssignMa(ma, [{ kunde_id: k.id, kunde_name: k.name }]);
                    } else {
                      onAddKunde(wpId, { kunde_id: k.id, kunde_name: k.name });
                    }
                  }}
                  className="w-full flex items-center justify-between px-4 py-2.5 rounded-lg border border-[var(--border-default,#E5E7EB)] hover:border-[#338edc] transition-colors text-left">
                  <div>
                    <p className="text-sm font-medium text-[var(--text-primary)]">{k.name}</p>
                    <p className="text-xs text-[var(--text-tertiary)]">{Math.round(k.soll_pakete)} {t('einteilung.pakete', 'packages')}</p>
                  </div>
                  <ChevronRight size={14} className="text-[var(--text-muted)]" />
                </button>
              ))
          )}
        </div>
      </div>
    </div>
  );
}
